import React, { useState, useEffect } from 'react';
import Auth from './pages/Auth';
import AdminUsers from './pages/AdminUsers';
import AdminClusters from './pages/AdminClusters';
import AdminCantieri from './pages/AdminCantieri';
import AdminConfig from './pages/AdminConfig';
import UserDashboard from './pages/UserDashboard';
import CantiereBrowser from './pages/CantiereBrowser';
import ObjectDashboard from './pages/ObjectDashboard';
import ManageIngestion from './pages/ManageIngestion';
import Documentation from './pages/Documentation';

import { Cantiere } from './types';
import { NotificationProvider, useNotification } from './components/NotificationContext';

const NotificationArea: React.FC = () => {
  const { message, type, clearNotification } = useNotification();

  if (!message) return null;

  const getStyle = () => {
    switch (type) {
      case 'success': return { background: '#4caf50', color: 'white' };
      case 'error': return { background: '#f44336', color: 'white' };
      default: return { background: '#2196f3', color: 'white' };
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      padding: '12px 24px',
      zIndex: 3000,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      ...getStyle()
    }}>
      <span>{message}</span>
      <button
        onClick={clearNotification}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          fontSize: '1.2rem',
          marginLeft: '16px'
        }}
      >
        <i className="fas fa-xmark"></i>
      </button>
    </div >
  );
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedCantiereId, setSelectedCantiereId] = useState<number | null>(null);
  const [selectedCantiere, setSelectedCantiere] = useState<Cantiere | null>(null);
  const [selectedObjectName, setSelectedObjectName] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const storedIsAdmin = localStorage.getItem('is_admin') === 'true';
    if (token) {
      setIsLoggedIn(true);
      setIsAdmin(storedIsAdmin);
      if (storedIsAdmin) {
        setCurrentView('dashboard');
      } else {
        setCurrentView('dashboard');
      }
    }
  }, []);

  const handleLoginSuccess = (adminStatus: boolean) => {
    setIsLoggedIn(true);
    setIsAdmin(adminStatus);
    localStorage.setItem('is_admin', adminStatus.toString());
    if (adminStatus) {
      setCurrentView('dashboard');
    } else {
      setCurrentView('dashboard');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('is_admin');
    setIsLoggedIn(false);
    setIsAdmin(false);
    setCurrentView('dashboard');
    setSelectedCantiereId(null);
    setSelectedObjectName(null);
  };

  const handleSelectCantiere = (cantiere: Cantiere) => {
    setSelectedCantiereId(cantiere.id);
    setSelectedCantiere(cantiere);
    setCurrentView('cantiereBrowser');
  };

  const handleSelectDigitalObject = (cantiereId: number, objectName: string) => {
    setSelectedCantiereId(cantiereId);
    setSelectedObjectName(objectName);
    setCurrentView('objectDashboard');
  };

  const handleBackToCantieri = () => {
    setCurrentView('dashboard');
    setSelectedCantiereId(null);
    setSelectedCantiere(null);
    setSelectedObjectName(null);
  };

  const handleBackToCantiereBrowser = () => {
    setCurrentView('cantiereBrowser');
    setSelectedObjectName(null);
  };

  const isAdminView = (view: string) => {
    return ['adminUsers', 'adminClusters', 'adminCantieri', 'manageIngestion', 'adminConfig'].includes(view);
  };

  const renderContent = () => {
    if (!isLoggedIn) {
      return <Auth onLoginSuccess={handleLoginSuccess} />;
    }

    switch (currentView) {
      case 'adminUsers':
      case 'adminClusters':
      case 'adminCantieri':
      case 'manageIngestion':
      case 'adminConfig':
        return (
          <div>
            <nav>
              <button className="btn btn-text" onClick={() => setCurrentView('adminClusters')}>Manage Clusters</button>
              <button className="btn btn-text" onClick={() => setCurrentView('adminCantieri')}>Manage Cantieri</button>
              <button className="btn btn-text" onClick={() => setCurrentView('manageIngestion')}>Manage Ingestion</button>
              <button className="btn btn-text" onClick={() => setCurrentView('adminConfig')}>Config</button>
              <button className="btn btn-text" onClick={() => setCurrentView('adminUsers')}>Manage Users</button>
            </nav>
            {currentView === 'adminUsers' && <AdminUsers />}
            {currentView === 'adminClusters' && <AdminClusters />}
            {currentView === 'adminCantieri' && <AdminCantieri onSelectCantiere={handleSelectCantiere} />}
            {currentView === 'manageIngestion' && <ManageIngestion />}
            {currentView === 'adminConfig' && <AdminConfig />}
          </div>
        );
      case 'cantiereBrowser':
        return selectedCantiere && (
          <CantiereBrowser
            cantiere={selectedCantiere}
            onSelectDigitalObject={handleSelectDigitalObject}
            onBack={handleBackToCantieri}
          />
        );
      case 'objectDashboard':
        return selectedCantiereId && selectedObjectName && (
          <ObjectDashboard
            cantiereId={selectedCantiere!.id}
            dpacCode={selectedCantiere!.dpac_code || ''}
            cantiereName={selectedCantiere!.name || ''}
            objectName={selectedObjectName}
            onBack={handleBackToCantiereBrowser}
            canEdit={selectedCantiere?.can_edit}
          />
        );
      case 'documentation':
        return <Documentation />;
      case 'dashboard':
      default:
        return <UserDashboard onSelectCantiere={handleSelectCantiere} isAdmin={isAdmin} />;
    }
  };

  return (
    <div className="App">
      <NotificationArea />
      {isLoggedIn && (
        <header className="app-header">
          <h1>SmartPack</h1>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {(currentView === 'documentation' || (isAdmin && isAdminView(currentView))) && (
              <button
                className="btn btn-text"
                style={{ color: 'white' }}
                onClick={() => setCurrentView('dashboard')}
              >
                DASHBOARD
              </button>
            )}
            {isAdmin && !isAdminView(currentView) && (
              <button
                className="btn btn-text"
                style={{ color: 'white' }}
                onClick={() => setCurrentView('adminCantieri')}
              >
                AMMINISTRAZIONE
              </button>
            )}
            <button
              className="btn btn-text"
              style={{ color: 'white', fontWeight: currentView === 'documentation' ? 'bold' : 'normal' }}
              onClick={() => setCurrentView('documentation')}
            >
              DOCUMENTAZIONE
            </button>
            <button className="btn btn-text" style={{ color: 'white' }} onClick={handleLogout}>Logout</button>
          </div>
        </header>
      )}
      <main className={isLoggedIn ? "container" : ""}>
        {renderContent()}
      </main>
    </div>
  );
}

const WrappedApp = () => (
  <NotificationProvider>
    <App />
  </NotificationProvider>
);

export default WrappedApp;